import React, { useState, useEffect } from "react";
import {
  Upload,
  FileText,
  MapPin,
  User,
  CreditCard,
  Hash,
  Building,
  CheckSquare,
  Square,
  File,
  Send,
} from "lucide-react";
import axios from "axios";

const OnboardingForm = () => {
  // Get candidate data from localStorage
  const [candidateData, setCandidateData] = useState(null);

  const [personalInfo, setPersonalInfo] = useState({
    lastName: "",
    firstName: "",
    middleName: "",
    mobileNo: "",
    alternativeNo: "",
    emailId: "",
    alternativeEmail: "",
    dob: "",
    gender: "",
    maritalStatus: "",
    whatsAppNo: "",
    telegramId: "",
    linkedinId: "",
  });

  // STEM Information State
  const [stemInfo, setStemInfo] = useState({
    visaType: "",
    visaStartDate: "",
    visaEndDate: "",
    uscisNo: "",
    cardNo: "",
    sevisSchoolCode: "",
    fieldOfStudy: "",
    stemId: "",
    dsoName: "",
    dsoNumber: "",
    dsoEmail: "",
  });

  // General Information State
  const [generalInfo, setGeneralInfo] = useState({
    currentAddress: "",
    emergencyContactName: "",
    emergencyContactNumber: "",
    emergencyContactEmail: "",
    relation: "",
  });

  // Payroll Information State
  const [payrollInfo, setPayrollInfo] = useState({
    ssn: "",
    address: "",
    bankName: "",
    bankAddress: "",
    accountNumber: "",
    routingNumber: "",
    accountType: "",
  });

  // Document Section State
  const documentConfig = {
    addressProof: { label: "Address Proof", icon: MapPin },
    voidCheck: { label: "Void Check", icon: CreditCard },
    ead: { label: "EAD", icon: FileText },
    updatedI20: { label: "Updated I-20", icon: User },
    i797: { label: "I-797", icon: Hash },
    ssnCopy: { label: "SSN Copy", icon: Building },
    w4: { label: "W4", icon: FileText },
    insuranceWaiverForm: { label: "Insurance Waiver Form", icon: FileText },
  };

  const [selectedDocuments, setSelectedDocuments] = useState({
    addressProof: { checked: false, file: null },
    voidCheck: { checked: false, file: null },
    ead: { checked: false, file: null },
    updatedI20: { checked: false, file: null },
    i797: { checked: false, file: null },
    ssnCopy: { checked: false, file: null },
    w4: { checked: false, file: null },
    insuranceWaiverForm: { checked: false, file: null },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load candidate data from localStorage on component mount
  useEffect(() => {
    const storedCandidateData = localStorage.getItem("candidateData");
    if (storedCandidateData) {
      try {
        const parsedData = JSON.parse(storedCandidateData);
        setCandidateData(parsedData.data);
      } catch (error) {
        console.error("Error parsing candidate data from localStorage:", error);
      }
    }
  }, []);

  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleStemInfoChange = (field, value) => {
    setStemInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleGeneralInfoChange = (field, value) => {
    setGeneralInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handlePayrollInfoChange = (field, value) => {
    setPayrollInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleDocumentToggle = (documentKey) => {
    setSelectedDocuments((prev) => ({
      ...prev,
      [documentKey]: {
        ...prev[documentKey],
        checked: !prev[documentKey].checked,
      },
    }));
  };

  const handleDocumentUpload = (file, documentKey) => {
    setSelectedDocuments((prev) => ({
      ...prev,
      [documentKey]: {
        ...prev[documentKey],
        file: {
          fileObject: file,
          name: file.name,
          size: file.size,
          type: file.type,
        },
      },
    }));
  };

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async () => {
    // return;
    try {
      setIsSubmitting(true);

      const joiningDocuments = Object.entries(selectedDocuments)
        .filter(([key, doc]) => doc.checked && doc.file)
        .map(([key, doc]) => ({
          documentType: key,
          fileName: doc.file.name,
          fileObject: doc.file.fileObject,
        }));

      if (joiningDocuments.length === 0) {
        alert("Please select and upload at least one document.");
        setIsSubmitting(false);
        return;
      }

      // Create query object with leadID and selected document labels
      // Fixed: Get selected document labels from selectedDocuments instead of joiningDocuments
      const selectedDocumentLabels = Object.entries(selectedDocuments)
        .filter(([key, doc]) => doc.checked)
        .map(([key, doc]) => documentConfig[key].label);

      const queryObject = {
        leadID: candidateData?.leadID,
        requestInvoiceId: candidateData?._id,
        selectedDocuments: selectedDocumentLabels,
      };

      // Convert query object to URL parameters
      const queryParams = new URLSearchParams({
        leadID: queryObject.leadID,
        requestInvoiceId: queryObject?.requestInvoiceId,
        selectedDocuments: JSON.stringify(queryObject.selectedDocuments),
      }).toString();

      // Append query parameters to uploadURL
      const uploadURLWithQuery = `${process.env.REACT_APP_DOC_SUBMISSION_URL}/main-onboarding-form?${queryParams}`;

      // Create FormData to send files
      const formData = new FormData();

      // Create candidate object with modified uploadURL
      const candidateDetails = {
        name: candidateData?.fullName,
        phone: candidateData?.phoneNumber,
        email: candidateData?.email,
        leadId: candidateData?.leadID,
        // requestInvoiceId: candidateData?.requestInvoiceId,
        uploadURL: uploadURLWithQuery,
      };
      formData.append("candidate", JSON.stringify(candidateDetails));

      // Create position object
      const positionData = {
        role: "Software Engineer",
        startDate: "2024-01-15",
        workAddress: "123 Tech Park, Silicon Valley",
        jobDescription: "Develop scalable applications.",
        companyLogo: "https:/imga",
      };
      formData.append("position", JSON.stringify(positionData));
      // Add files and document metadata
      joiningDocuments.forEach((doc, index) => {
        formData.append(`documents[${index}][documentType]`, doc.documentType);
        formData.append(`documents[${index}][fileName]`, doc.fileName);
        formData.append(`file_${doc.documentType}`, doc.fileObject);
      });

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/offer_letter/Send_documents/send`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      alert("Documents sent successfully!");
    } catch (error) {
      console.error("Error sending documents:", error);
      alert("Error sending documents. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full mx-auto p-6 bg-white">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Employee Onboarding Form
        </h1>
        <p className="text-gray-600">
          Please fill out all required information for onboarding process
        </p>
        {candidateData && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-medium text-blue-900 mb-2">
              Candidate Information
            </h3>
            <p className="text-blue-800 text-sm">
              <strong>Name:</strong> {candidateData.fullName} |{" "}
              <strong>Email:</strong> {candidateData.email} |{" "}
              <strong>Phone:</strong> {candidateData.phoneNumber}
            </p>
          </div>
        )}
      </div>

      <div className="space-y-8">
        {/* Personal Information Section */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b border-gray-200 pb-2">
            Personal Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                value={personalInfo.firstName}
                onChange={(e) =>
                  handlePersonalInfoChange("firstName", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Middle Name
              </label>
              <input
                type="text"
                value={personalInfo.middleName}
                onChange={(e) =>
                  handlePersonalInfoChange("middleName", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                value={personalInfo.lastName}
                onChange={(e) =>
                  handlePersonalInfoChange("lastName", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mobile No *
              </label>
              <input
                type="tel"
                value={personalInfo.mobileNo}
                onChange={(e) =>
                  handlePersonalInfoChange("mobileNo", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alternative No
              </label>
              <input
                type="tel"
                value={personalInfo.alternativeNo}
                onChange={(e) =>
                  handlePersonalInfoChange("alternativeNo", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email ID *
              </label>
              <input
                type="email"
                value={personalInfo.emailId}
                onChange={(e) =>
                  handlePersonalInfoChange("emailId", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alternative Email
              </label>
              <input
                type="email"
                value={personalInfo.alternativeEmail}
                onChange={(e) =>
                  handlePersonalInfoChange("alternativeEmail", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                DOB *
              </label>
              <input
                type="date"
                value={personalInfo.dob}
                onChange={(e) =>
                  handlePersonalInfoChange("dob", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gender *
              </label>
              <select
                value={personalInfo.gender}
                onChange={(e) =>
                  handlePersonalInfoChange("gender", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marital Status
              </label>
              <select
                value={personalInfo.maritalStatus}
                onChange={(e) =>
                  handlePersonalInfoChange("maritalStatus", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Status</option>
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="widowed">Widowed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WhatsApp No
              </label>
              <input
                type="tel"
                value={personalInfo.whatsAppNo}
                onChange={(e) =>
                  handlePersonalInfoChange("whatsAppNo", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telegram ID
              </label>
              <input
                type="text"
                value={personalInfo.telegramId}
                onChange={(e) =>
                  handlePersonalInfoChange("telegramId", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                LinkedIn ID
              </label>
              <input
                type="text"
                value={personalInfo.linkedinId}
                onChange={(e) =>
                  handlePersonalInfoChange("linkedinId", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* STEM Information Section */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b border-gray-200 pb-2">
            STEM Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visa Type
              </label>
              <input
                type="text"
                value={stemInfo.visaType}
                onChange={(e) =>
                  handleStemInfoChange("visaType", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visa Start Date
              </label>
              <input
                type="date"
                value={stemInfo.visaStartDate}
                onChange={(e) =>
                  handleStemInfoChange("visaStartDate", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visa End Date
              </label>
              <input
                type="date"
                value={stemInfo.visaEndDate}
                onChange={(e) =>
                  handleStemInfoChange("visaEndDate", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                USCIS NO
              </label>
              <input
                type="text"
                value={stemInfo.uscisNo}
                onChange={(e) =>
                  handleStemInfoChange("uscisNo", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Card NO
              </label>
              <input
                type="text"
                value={stemInfo.cardNo}
                onChange={(e) => handleStemInfoChange("cardNo", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SEVIS School Code
              </label>
              <input
                type="text"
                value={stemInfo.sevisSchoolCode}
                onChange={(e) =>
                  handleStemInfoChange("sevisSchoolCode", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Field Of Study (CIP Name & Code)
              </label>
              <input
                type="text"
                value={stemInfo.fieldOfStudy}
                onChange={(e) =>
                  handleStemInfoChange("fieldOfStudy", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                STEM ID
              </label>
              <input
                type="text"
                value={stemInfo.stemId}
                onChange={(e) => handleStemInfoChange("stemId", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                DSO Name
              </label>
              <input
                type="text"
                value={stemInfo.dsoName}
                onChange={(e) =>
                  handleStemInfoChange("dsoName", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                DSO Number
              </label>
              <input
                type="tel"
                value={stemInfo.dsoNumber}
                onChange={(e) =>
                  handleStemInfoChange("dsoNumber", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                DSO Email
              </label>
              <input
                type="email"
                value={stemInfo.dsoEmail}
                onChange={(e) =>
                  handleStemInfoChange("dsoEmail", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* General Information Section */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b border-gray-200 pb-2">
            General Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Address (Street, City, State, Zipcode)
              </label>
              <textarea
                value={generalInfo.currentAddress}
                onChange={(e) =>
                  handleGeneralInfoChange("currentAddress", e.target.value)
                }
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emergency Contact Name
                </label>
                <input
                  type="text"
                  value={generalInfo.emergencyContactName}
                  onChange={(e) =>
                    handleGeneralInfoChange(
                      "emergencyContactName",
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emergency Contact Number
                </label>
                <input
                  type="tel"
                  value={generalInfo.emergencyContactNumber}
                  onChange={(e) =>
                    handleGeneralInfoChange(
                      "emergencyContactNumber",
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emergency Contact Email
                </label>
                <input
                  type="email"
                  value={generalInfo.emergencyContactEmail}
                  onChange={(e) =>
                    handleGeneralInfoChange(
                      "emergencyContactEmail",
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relation
                </label>
                <input
                  type="text"
                  value={generalInfo.relation}
                  onChange={(e) =>
                    handleGeneralInfoChange("relation", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Payroll Information Section */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b border-gray-200 pb-2">
            Payroll Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SSN
              </label>
              <input
                type="text"
                value={payrollInfo.ssn}
                onChange={(e) => handlePayrollInfoChange("ssn", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength="11"
                placeholder="XXX-XX-XXXX"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                value={payrollInfo.address}
                onChange={(e) =>
                  handlePayrollInfoChange("address", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Name
              </label>
              <input
                type="text"
                value={payrollInfo.bankName}
                onChange={(e) =>
                  handlePayrollInfoChange("bankName", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Address
              </label>
              <input
                type="text"
                value={payrollInfo.bankAddress}
                onChange={(e) =>
                  handlePayrollInfoChange("bankAddress", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Number
              </label>
              <input
                type="text"
                value={payrollInfo.accountNumber}
                onChange={(e) =>
                  handlePayrollInfoChange("accountNumber", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Routing Number
              </label>
              <input
                type="text"
                value={payrollInfo.routingNumber}
                onChange={(e) =>
                  handlePayrollInfoChange("routingNumber", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength="9"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Type
              </label>
              <select
                value={payrollInfo.accountType}
                onChange={(e) =>
                  handlePayrollInfoChange("accountType", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Account Type</option>
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
              </select>
            </div>
          </div>
        </div>

        {/* Document Section */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b border-gray-200 pb-2">
            Document Section
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Side - Document Checkboxes */}
            <div>
              <h3 className="text-lg font-medium mb-4">Required Documents</h3>
              <div className="space-y-3">
                {Object.entries(documentConfig).map(([key, config]) => {
                  const IconComponent = config.icon;
                  const isChecked = selectedDocuments[key].checked;

                  return (
                    <div
                      key={key}
                      className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <button
                        type="button"
                        onClick={() => handleDocumentToggle(key)}
                        className="flex-shrink-0"
                      >
                        {isChecked ? (
                          <CheckSquare className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                      <IconComponent className="w-5 h-5 text-gray-600 flex-shrink-0" />
                      <span
                        className={`font-medium ${
                          isChecked ? "text-blue-600" : "text-gray-700"
                        }`}
                      >
                        {config.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Side - File Upload Areas */}
            <div>
              <h3 className="text-lg font-medium mb-4">Upload Documents</h3>
              <div className="space-y-4">
                {Object.entries(selectedDocuments)
                  .filter(([key, doc]) => doc.checked)
                  .map(([key, doc]) => {
                    const config = documentConfig[key];
                    const IconComponent = config.icon;

                    return (
                      <div
                        key={key}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center mb-3">
                          <IconComponent className="w-5 h-5 text-gray-600 mr-2" />
                          <h4 className="font-medium text-gray-900">
                            {config.label}
                          </h4>
                        </div>

                        {doc.file ? (
                          <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center">
                              <File className="w-4 h-4 text-green-600 mr-2" />
                              <span className="text-sm text-green-800">
                                {doc.file.name}
                              </span>
                            </div>
                            <span className="text-xs text-green-600">
                              {(doc.file.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                        ) : (
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  handleDocumentUpload(file, key);
                                }
                              }}
                              className="hidden"
                              id={`upload-${key}`}
                            />
                            <label
                              htmlFor={`upload-${key}`}
                              className="cursor-pointer"
                            >
                              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                              <p className="text-sm text-gray-600">
                                Click to upload {config.label}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                PDF, DOC, DOCX, JPG, PNG (Max 10MB)
                              </p>
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })}
                {Object.values(selectedDocuments).every(
                  (doc) => !doc.checked
                ) && (
                  <div className="text-center py-12 text-gray-500">
                    <File className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg">
                      Select documents from the left to upload
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg flex items-center transition-colors font-medium"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Send Onboarding Form
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingForm;
